/**
 * Рабочий контекст в адресе: проект и фича.
 *
 * Норма: [app-shell-shell-routing-R12] — адрес определяет рабочий контекст
 * полностью. Проектные разделы несут проект в адресе; открытие адреса
 * восстанавливает тот же проект, смена проекта или фичи в оболочке меняет
 * адрес. Названный в адресе проект не подменяется никогда: адрес без проекта
 * дополняется проектом по умолчанию (administration-projects-R07) и сразу его
 * отражает, а проект несуществующий или невидимый актору ведёт себя как
 * «не найдено» ([rbac-R23]) — подмены доступным не происходит. Фича
 * необязательна, а фича не из проекта адреса обрабатывается как её отсутствие,
 * но не молча: пользователю сообщается, что открыта другая
 * ([business-model-features-R10, R11]).
 * Явный класс маппингов при неверном контексте — отказ без подмены
 * ([frontend-class-mappings-R34]).
 *
 * Решение о контексте вынесено в чистую функцию `resolveProjectContext`:
 * разбор адреса от применения решения отделён, поэтому правило проверяется без
 * маршрутизатора и хранилищ.
 */

import { watch } from "vue";
import type { LocationQueryRaw, RouteLocationNormalizedLoaded, Router } from "vue-router";
import { ElMessage } from "element-plus";
import { useProjectsStore } from "@/entities/projects";
import { useFeaturesStore } from "@/entities/features";
import { i18n } from "@/shared/i18n";
import { installRestrictedWorkContext } from './restricted-work-context';

/** Имена параметров адреса, несущих рабочий контекст. */
export const PROJECT_QUERY_PARAM = "project";
export const FEATURE_QUERY_PARAM = "feature";

/** Что сообщается пользователю, если ссылка привела не туда (R12). */
export type TProjectContextNotice =
  | "project-not-found"
  | "feature-not-in-project"
  | null;

interface IIdentified {
  readonly id: string;
}

export interface IProjectContextInput {
  /** Раздел зависит от проекта (несёт его в адресе). */
  readonly projectScoped: boolean;
  /** Раздел работает в контексте фичи (фича в адресе необязательна). */
  readonly featureScoped: boolean;
  /**
   * Разбор вызван переходом по адресу, а не действием в оболочке.
   *
   * Два представления контекста не расходятся ни в одну сторону (R12), но при
   * расхождении кто-то из них новее: переход по ссылке меняет выбор, а выбор
   * в селекторе меняет адрес. Отсюда и признак.
   */
  readonly addressChanged: boolean;
  /**
   * Фича из адреса уже сверена с перечнем этого проекта.
   *
   * Пока не сверена, ведёт адрес: перечень приходит позже проекта, и
   * автоматический выбор новейшей HEAD не должен затирать фичу из ссылки.
   */
  readonly addressFeatureHandled: boolean;
  /** Значения параметров адреса; пустая строка — параметра нет. */
  readonly queryProjectId: string;
  readonly queryFeatureId: string;
  /** R34: явный класс маппингов запрещает подмену фичи ссылки. */
  readonly mappingClassAddressed?: boolean;
  /** Проекты, видимые актору: невидимый неотличим от несуществующего. */
  readonly projects: readonly IIdentified[];
  readonly selectedProjectId: string;
  /** Проект, которому принадлежит загруженный перечень фич. */
  readonly featuresProjectId: string;
  /** Опции селектора фичи — перечень плюс архивная выбранная (features-R11). */
  readonly featureOptions: readonly IIdentified[];
  readonly selectedFeatureId: string;
}

/** Изменение параметра адреса: `null` — параметр убрать. */
export interface IProjectContextQueryPatch {
  readonly project?: string | null;
  readonly feature?: string | null;
}

export interface IProjectContextDecision {
  /** Новый выбор проекта; `null` — выбор не меняется, `""` — снимается. */
  readonly selectProjectId: string | null;
  readonly selectFeatureId: string | null;
  readonly notice: TProjectContextNotice;
  readonly queryPatch: IProjectContextQueryPatch | null;
  /** Фича адреса сверена с перечнем проекта: дальше ведёт оболочка. */
  readonly featureResolved: boolean;
}

const NOTHING_TO_DO: IProjectContextDecision = {
  selectProjectId: null,
  selectFeatureId: null,
  notice: null,
  queryPatch: null,
  featureResolved: false,
};

/**
 * Решение о рабочем контексте по адресу и состоянию — чистая функция
 * (app-shell-shell-routing-R12, business-model-features-R10).
 */
export function resolveProjectContext(
  input: IProjectContextInput,
): IProjectContextDecision {
  if (input.projectScoped === false) return NOTHING_TO_DO;
  // Перечень проектов ещё не пришёл: судить о том, существует ли проект
  // адреса и виден ли он актору, не по чему.
  if (input.projects.length === 0) return NOTHING_TO_DO;

  let selectProjectId: string | null = null;
  let effectiveProjectId = input.selectedProjectId;

  // Адрес ведёт, когда по нему только что перешли или когда проект ещё не
  // выбран; иначе новее выбор, сделанный в оболочке, и адрес идёт за ним.
  const addressLeadsProject =
    input.addressChanged === true || input.selectedProjectId.length === 0;

  if (input.queryProjectId.length > 0 && addressLeadsProject === true) {
    const known = input.projects.some(
      (project) => project.id === input.queryProjectId,
    );
    if (known === false) {
      // Проект адреса не существует либо не виден актору — одно и то же
      // (rbac-R23). Подмены доступным проектом не происходит, адрес остаётся
      // как есть: иначе пустой раздел чужого проекта был бы неотличим от
      // незаполненного своего.
      return {
        selectProjectId: input.selectedProjectId.length > 0 ? "" : null,
        selectFeatureId: null,
        notice: "project-not-found",
        queryPatch: null,
        featureResolved: false,
      };
    }
    if (input.selectedProjectId !== input.queryProjectId) {
      selectProjectId = input.queryProjectId;
      effectiveProjectId = input.queryProjectId;
    }
  } else if (input.selectedProjectId.length === 0) {
    // Адрес проекта не называет — подменять нечего: выбирается проект по
    // умолчанию, первый в перечне (projects-R07), и он сразу отражается в
    // адресе.
    const fallback = input.projects[0];
    if (fallback !== undefined) {
      selectProjectId = fallback.id;
      effectiveProjectId = fallback.id;
    }
  }

  let selectFeatureId: string | null = null;
  let notice: TProjectContextNotice = null;
  /** `undefined` — параметр фичи не трогаем. */
  let featureParam: string | null | undefined = undefined;

  // Фича решается только тогда, когда перечень принадлежит проекту адреса:
  // до этого любое суждение о принадлежности фичи было бы о чужом перечне.
  const listBelongsToProject =
    effectiveProjectId.length > 0 &&
    input.featuresProjectId === effectiveProjectId;

  // Пока фича адреса не сверена с перечнем, ведёт адрес: перечень приходит
  // позже проекта, и автоматический выбор новейшей HEAD не должен затирать
  // фичу из ссылки. После сверки новее выбор в селекторе.
  const addressLeadsFeature =
    input.addressChanged === true || input.addressFeatureHandled === false;
  const featureResolved =
    input.featureScoped === true && listBelongsToProject === true;

  if (input.featureScoped === true && featureResolved === false) {
    // Перечня проекта ещё нет. Фича адреса ждёт его, только если адрес назвал
    // тот же проект: тогда она — то, что прислали. Если же проект в адресе
    // другой (его меняют в оболочке) или не назван вовсе, фича в адресе
    // осталась от прежнего проекта — она убирается сразу, не дожидаясь
    // перечня, иначе продукт принял бы собственный след за присланную ссылку.
    if (input.queryProjectId !== effectiveProjectId) featureParam = null;
  }

  if (featureResolved === true) {
    // frontend-class-mappings-R34: чужая фича делает ссылку на класс
    // недействительной. Сохраняем адрес для отказа страницы, снимаем
    // автоматический выбор и ждём явного выбора пользователя.
    if (input.mappingClassAddressed === true &&
      input.queryProjectId === effectiveProjectId &&
      input.queryFeatureId.length > 0 &&
      (addressLeadsFeature || input.selectedFeatureId.length === 0) &&
      !input.featureOptions.some(feature => feature.id === input.queryFeatureId)) {
      return {
        selectProjectId,
        selectFeatureId: input.selectedFeatureId.length > 0 ? "" : null,
        notice: null,
        queryPatch: null,
        featureResolved: true,
      };
    }
    let effectiveFeatureId = input.selectedFeatureId;
    if (
      addressLeadsFeature === true &&
      input.queryFeatureId.length > 0 &&
      input.queryFeatureId !== input.selectedFeatureId
    ) {
      const belongs = input.featureOptions.some(
        (feature) => feature.id === input.queryFeatureId,
      );
      if (belongs === true) {
        selectFeatureId = input.queryFeatureId;
        effectiveFeatureId = input.queryFeatureId;
      } else {
        // Фича не из проекта адреса — то же, что её отсутствие (features-R10),
        // но пользователю сообщается: он должен знать, что смотрит не то, что
        // ему прислали. Адрес приводится к фактически выбранной фиче.
        notice = "feature-not-in-project";
      }
    }
    featureParam = effectiveFeatureId.length > 0 ? effectiveFeatureId : null;
  }

  const patch: { project?: string | null; feature?: string | null } = {};
  if (
    effectiveProjectId.length > 0 &&
    input.queryProjectId !== effectiveProjectId
  ) {
    patch.project = effectiveProjectId;
  }
  if (featureParam === null) {
    if (input.queryFeatureId.length > 0) patch.feature = null;
  } else if (featureParam !== undefined && featureParam !== input.queryFeatureId) {
    patch.feature = featureParam;
  }

  return {
    selectProjectId,
    selectFeatureId,
    notice,
    queryPatch: Object.keys(patch).length > 0 ? patch : null,
    featureResolved,
  };
}

/** Единственное значение параметра адреса; список значений — не контекст. */
function readQueryParam(
  route: RouteLocationNormalizedLoaded,
  name: string,
): string {
  const value = route.query[name];
  return typeof value === "string" ? value : "";
}

/**
 * Связывает адрес и рабочий контекст в обе стороны (R12). Ставится один раз
 * при запуске приложения — до монтирования оболочки, чтобы решение об адресе
 * принималось раньше, чем оболочка подхватит выбор и загрузит зависимое.
 */
export function installProjectContext(router: Router): void {
  const projectsStore = useProjectsStore();
  const featuresStore = useFeaturesStore();
  const reconcileRestricted = installRestrictedWorkContext(router);

  /** Отказ по одному и тому же проекту сообщается один раз. */
  let reportedMissingProject = "";
  /** Пара «проект перечня + фича адреса», уже сверенная с перечнем. */
  let handledAddressFeature = "";
  /**
   * Адрес, поправка к которому уже отправлена и ещё не доехала.
   *
   * Разбор читает текущий адрес маршрутизатора, а поправка применяется не
   * мгновенно: пока она в пути, `currentRoute` остаётся прежним. Без этой
   * памяти следующий разбор — а его поднимает и сам переход, и любое
   * изменение сторов — видит прежний адрес, вычисляет **ту же** поправку и
   * отправляет её снова. Повторная отправка того же адреса маршрутизатором
   * отвергается как переход в ту же точку, но обработчик перехода при этом
   * всё равно срабатывает — и круг замыкается: приложение не приходит в
   * покой. Отсюда правило: одна и та же поправка в пути не дублируется.
   */
  let pendingAddress: string | null = null;
  /**
   * Сколько переходов сейчас в пути.
   *
   * Разбор читает адрес маршрутизатора, а во время перехода этот адрес — ещё
   * прежний, покинутый. Поправка, вычисленная по нему, ведёт назад, в
   * покинутый раздел, и отменяет начатый переход: пользователь кликает по
   * пункту меню, а раздел не открывается. Ждать нечего — переход завершится и
   * сам поднимет разбор (`afterEach`), уже по новому адресу.
   *
   * Признака, поднимаемого в `beforeEach`, для этого мало: до первой проверки
   * доступа проходит несколько микрозадач, и поправка, вычисленная в это окно,
   * успевает отменить только что начатый переход. Поэтому переходы считаются с
   * момента вызова.
   */
  let navigationsInFlight = 0;

  function counted<T>(run: () => Promise<T>): Promise<T> {
    navigationsInFlight += 1;
    return run().finally(() => {
      navigationsInFlight -= 1;
    });
  }

  const pushRoute = router.push.bind(router);
  const replaceRoute = router.replace.bind(router);
  router.push = (
    to: Parameters<Router["push"]>[0],
  ): ReturnType<Router["push"]> => counted(() => pushRoute(to));
  router.replace = (
    to: Parameters<Router["replace"]>[0],
  ): ReturnType<Router["replace"]> => counted(() => replaceRoute(to));

  function notify(notice: TProjectContextNotice, missingProjectId: string): void {
    if (notice === "project-not-found") {
      if (reportedMissingProject === missingProjectId) return;
      reportedMissingProject = missingProjectId;
      ElMessage.error(i18n.global.t("project.notFoundInLink"));
      return;
    }
    if (notice === "feature-not-in-project") {
      ElMessage.warning(i18n.global.t("feature.notInLinkProject"));
    }
  }

  function reconcile(addressChanged: boolean): void {
    if (reconcileRestricted(addressChanged)) return;
    const route = router.currentRoute.value;
    // frontend-class-mappings-R30/R32/R33: the mapping URL has explicit keys.
    const mappingScoped = route.meta['mappingScoped'] === true;
    const projectParam = mappingScoped ? 'projectId' : PROJECT_QUERY_PARAM;
    const featureParam = mappingScoped ? 'featureId' : FEATURE_QUERY_PARAM;
    const queryProjectId = readQueryParam(route, projectParam);
    const queryFeatureId = readQueryParam(route, featureParam);
    if (queryProjectId !== reportedMissingProject) reportedMissingProject = "";
    const addressFeatureKey = `${featuresStore.projectId}|${queryFeatureId}`;

    const decision = resolveProjectContext({
      projectScoped: route.meta["projectScoped"] === true,
      featureScoped: route.meta["featureScoped"] === true,
      addressChanged,
      addressFeatureHandled: handledAddressFeature === addressFeatureKey,
      queryProjectId,
      queryFeatureId,
      mappingClassAddressed: mappingScoped && route.query['classId'] !== undefined,
      projects: projectsStore.projects,
      selectedProjectId: projectsStore.selectedProjectId,
      featuresProjectId: featuresStore.projectId,
      featureOptions: featuresStore.selectorOptions,
      selectedFeatureId: featuresStore.selectedFeatureId,
    });

    notify(decision.notice, queryProjectId);
    if (decision.featureResolved === true) {
      handledAddressFeature = addressFeatureKey;
    }

    if (decision.selectProjectId !== null) {
      projectsStore.selectProject(decision.selectProjectId);
    }
    if (decision.selectFeatureId !== null) {
      featuresStore.selectFeature(decision.selectFeatureId);
    }
    if (decision.queryPatch !== null) {
      const query: LocationQueryRaw = { ...route.query };
      const patch = decision.queryPatch;
      if (patch.project !== undefined) {
        query[projectParam] = patch.project;
      }
      if (patch.feature === null) {
        delete query[featureParam];
      } else if (patch.feature !== undefined) {
        query[featureParam] = patch.feature;
      }
      if (mappingScoped && addressChanged === false &&
        ((patch.project !== undefined && queryProjectId.length > 0) ||
         (patch.feature !== undefined && queryFeatureId.length > 0))) {
        delete query['classId'];
        delete query['dataset'];
      }
      const target = { path: route.path, query, hash: route.hash };
      const targetAddress = router.resolve(target).fullPath;

      // Разбор идемпотентен: при уже верном адресе второй проход ничего не
      // меняет. Поправка «в самого себя» переходом не является.
      if (targetAddress === route.fullPath) return;
      // Такая же поправка уже в пути — второй раз её слать нечего (см.
      // `pendingAddress`).
      if (targetAddress === pendingAddress) return;

      // Приведение адреса к контексту — поправка, а не переход: history не
      // растёт, кнопка «назад» ведёт туда же, куда вела бы без поправки.
      pendingAddress = targetAddress;
      void router
        .replace(target)
        .catch(() => undefined)
        .finally(() => {
          if (pendingAddress === targetAddress) pendingAddress = null;
        });
    }
  }

  /** Изменилось состояние оболочки: адрес идёт за ним. */
  function reconcileFromState(): void {
    if (navigationsInFlight > 0) return;
    reconcile(false);
  }

  router.afterEach(() => {
    reconcile(true);
  });
  watch(() => projectsStore.projects, reconcileFromState);
  watch(() => projectsStore.selectedProjectId, reconcileFromState);
  watch(() => featuresStore.projectId, reconcileFromState);
  watch(() => featuresStore.features, reconcileFromState);
  watch(() => featuresStore.selectedFeatureId, reconcileFromState);
}
